import Clickable from 'components/Common/Clickable'
import FormatCurrency from 'components/Common/FormatCurrency'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import { useAppSelector } from 'hooks/reduxHooks'
import { getIsLuxPlusEnabled } from 'luxPlus/selectors/featureToggle'
import type { ComponentProps } from 'react'
import { useCallback } from 'react'
import * as React from 'react'
import { FormattedMessage } from 'react-intl'
import LuxPlusLogo from 'luxPlus/components/Logos/LuxPlusLogo'
import LuxPlusBookingProtectionLabelLabel from 'luxPlus/components/LuxPlusBookingProtectionLabel'
import LuxPlusInsuranceLabel from 'luxPlus/components/LuxPlusInsuranceLabel'
import _BaseLabel from './_BaseLabel'
import styled from 'styled-components'

const LOGO_HEIGHT = 10

const LuxPlusLabelElement = styled(_BaseLabel)`
  background-color: ${props => props.theme.palette.product.luxPlus.background};
  border-color: ${props => props.theme.palette.product.luxPlus.background};
  color: ${props => props.theme.palette.product.luxPlus.contrast};
`

interface Props extends Utils.PropsWithDataAttributes {
  type: App.LuxPlusLabelType
  saveAmount?: number
  hideSaveAmountPlus?: boolean
  inclusionDescription?: string
  onClick?: () => void
  to?: ComponentProps<typeof Clickable>['to']
}

function LuxPlusLabel(props: Props) {
  const {
    inclusionDescription,
    type,
    onClick,
    saveAmount,
    hideSaveAmountPlus,
    to,
    ...rest
  } = props

  const luxPlusEnabled = useAppSelector(getIsLuxPlusEnabled)
  const clickHandler = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    if (!onClick) return
    e.preventDefault()
    e.stopPropagation()
    onClick()
  }, [onClick])

  if (!luxPlusEnabled) return null

  const saveAmountSuffix = hideSaveAmountPlus ? '' : '+'

  const label = <LuxPlusLabelElement
    data-testid="lux-plus-label"
    {...rest}
    endIcon={onClick ? <LineInfoCircleIcon /> : undefined}
  >
    {type === 'bonus-inclusions' && !inclusionDescription && <FormattedMessage
      id="OXS7m77_WNp7-9yj1N1H"
      defaultMessage="Bonus inclusions with {logo}"
      description="Label for bonus inclusions available with LuxPlus+ membership"
      values={{ logo: <LuxPlusLogo colour="inherit" height={LOGO_HEIGHT} /> }}
    />}
    {type === 'bonus-inclusions' && !!inclusionDescription && <FormattedMessage
      id="31PC5vk6YX2bPgu1HXtd"
      defaultMessage="{description} with {logo}"
      description="Label for custom bonus inclusions description with LuxPlus+ logo"
      values={{ description: inclusionDescription, logo: <LuxPlusLogo colour="inherit" height={LOGO_HEIGHT} /> }}
    />}
    {type === 'member' && <FormattedMessage
      id="LqeGDlONUe7cK3RCwLAe"
      defaultMessage="{logo} member"
      description="Label indicating LuxPlus+ membership status for the membership"
      values={{ logo: <LuxPlusLogo colour="inherit" height={LOGO_HEIGHT} /> }}
    />}
    {type === 'only-logo' && <LuxPlusLogo colour="inherit" height={LOGO_HEIGHT} />}
    {type === 'early-access-offer' && <FormattedMessage
      id="SmkL8grGGWz9G77r3j4T"
      defaultMessage="Early access with {logo}"
      description="Label for early access offer available with LuxPlus+ membership"
      values={{ logo: <LuxPlusLogo colour="inherit" height={LOGO_HEIGHT} /> }}
    />}
    {type === 'member-only-offer' && <FormattedMessage
      id="JrNCTRM29GVguPsz02zY"
      defaultMessage="{logo} member-only offer"
      description="Label for offer exclusively available to LuxPlus+ members"
      values={{ logo: <LuxPlusLogo colour="inherit" height={LOGO_HEIGHT} /> }}
    />}
    {type === 'member-only-discount' && <FormattedMessage
      id="MyI_Co-dMTx3MsUDzTp5"
      defaultMessage="{logo} member-only discount"
      description="Label for discount exclusively available to LuxPlus+ members"
      values={{ logo: <LuxPlusLogo colour="inherit" height={LOGO_HEIGHT} /> }}
    />}
    {type === 'member-price-offer' && !!saveAmount && <FormattedMessage
      id="yG8-sK8jiKIX-_Lf8fns"
      defaultMessage="Save {formattedSaveAmount} with {logo}"
      description="Label showing LuxPlus+ member savings amount on an offer"
      values={{
        formattedSaveAmount: <><FormatCurrency format="roundedDollar" value={saveAmount} />{saveAmountSuffix}</>,
        logo: <LuxPlusLogo colour="inherit" height={LOGO_HEIGHT} />,
      }}
    />}
    {type === 'member-price-offer-cruise' && <FormattedMessage
      id="njgIffwG8jGiVQtUVt72"
      defaultMessage="{logo} discount available"
      description="Label indicating LuxPlus+ discount is available for a cruise offer"
      values={{ logo: <LuxPlusLogo colour="inherit" height={LOGO_HEIGHT} /> }}
    />}
    {type === 'member-price-offer-lpp' && !!saveAmount && <FormattedMessage
      id="ZgpitutO8uJvxV_WnBLA"
      defaultMessage="Save {formattedSaveAmount} with {logo}"
      description="Label showing LuxPlus+ member savings amount on a bedbank offer"
      values={{
        formattedSaveAmount: <><FormatCurrency format="roundedDollar" value={saveAmount} />{saveAmountSuffix}</>,
        logo: <LuxPlusLogo colour="inherit" height={LOGO_HEIGHT} />,
      }}
    />}
    {type === 'member-price-offer-tour' && !!saveAmount && <FormattedMessage
      id="5zSuQiiJi_1-DaMaQRsZ"
      defaultMessage="Save {formattedSaveAmount}/person with {logo}"
      description="Label showing LuxPlus+ per-person member savings amount on a tour"
      values={{
        formattedSaveAmount: <><FormatCurrency format="roundedDollar" value={saveAmount} />{saveAmountSuffix}</>,
        logo: <LuxPlusLogo colour="inherit" height={LOGO_HEIGHT} />,
      }}
    />}
    {type === 'options-available-tour' && <FormattedMessage
      id="XAJJIOTUNBekdLVgbPGO"
      defaultMessage="{logo} options available"
      description="Label indicating LuxPlus+ pricing options are available for a tour"
      values={{ logo: <LuxPlusLogo colour="inherit" height={LOGO_HEIGHT} /> }}
    />}
    {type === 'plus' && <LuxPlusLogo colour="inherit" type="plus-only" height={LOGO_HEIGHT} />}
    {type === 'plus-preview' && <FormattedMessage
      id="CoLeUQmp9AUbIvMIEFnn"
      defaultMessage="{logo} preview"
      description="Label indicating user is on a LuxPlus+ free preview membership in the site header"
      values={{ logo: <LuxPlusLogo colour="inherit" type="plus-only" height={LOGO_HEIGHT} /> }}
    />}
    {type === 'insurance' && !!saveAmount && <LuxPlusInsuranceLabel saveAmount={saveAmount} logoHeight={LOGO_HEIGHT} />}
    {type === 'booking-protection' && !!saveAmount && <LuxPlusBookingProtectionLabelLabel saveAmount={saveAmount} logoHeight={LOGO_HEIGHT} />}
    {type === 'savings' && !!saveAmount && <FormattedMessage
      id="3aEGrp8JsLihhAF0oo1P"
      defaultMessage="Extra {formattedSaveAmount} saved with {logo}"
      description="Label showing extra savings achieved with LuxPlus+ membership"
      values={{
        formattedSaveAmount: <FormatCurrency format="roundedDollar" value={saveAmount} />,
        logo: <LuxPlusLogo colour="inherit" height={LOGO_HEIGHT} />,
      }}
    />}
    {type === 'member-inclusions' && <FormattedMessage
      id="6zoPC0xA1-A1hjHpW0sI"
      defaultMessage="Member inclusions with {logo}"
      description="Label for member inclusions available with LuxPlus+ membership"
      values={{ logo: <LuxPlusLogo colour="inherit" height={LOGO_HEIGHT} /> }}
    />}
  </LuxPlusLabelElement>

  if (onClick || to) return <Clickable onClick={clickHandler} to={to} data-testid="lux-plus-label-clickable">{label}</Clickable>

  return label
}

export default LuxPlusLabel
